import { useMemo } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import { compose } from 'core/libs/recompose';
import { withRouter } from 'core/libs/router';

import { menuPropTypes } from 'core/utils/prop-types/relationships';
import { resolveScopedStyles } from 'core/utils/styled-jsx';
import { getCurrentMenuMatches } from 'core/utils/url-helper';

import withBreakpoint from 'core/hocs/withBreakpoint';
import withTheme from 'core/hocs/withTheme';
import withMenu from 'core/hocs/withMenu';

import Link from 'core/components/Link';
import Scroller from 'core/components/Scroller';
import EmptyWrapper from 'core/components/EmptyWrapper';

const emptyItemsRef = [];

function ScooterMenu(props) {
  const {
    menu,
    additionalMenuItems,
    additionalMenuItemsIndex,
    currentItem,
    isAmp,
    menuListClassName,
    isDesktop,
    theme,
    theme: {
      controls: {
        scooter: scooterAtoms,
      },
    },
    location,
  } = props;

  let items = menu?.data?.attributes.menu_items ?? emptyItemsRef;
  if (additionalMenuItems) {
    if (additionalMenuItemsIndex) {
      items = items.slice(); // делаем копию потому что splice мутирует обьект
      items.splice(additionalMenuItemsIndex, 0, ...additionalMenuItems);
    } else {
      items = items.concat(additionalMenuItems);
    }
  }

  const currentItemResolved = useMemo(
    () => {
      if (!items.length) return null;
      return currentItem || getCurrentMenuMatches(items, location, isAmp)[0];
    },
    [location, items, currentItem, isAmp]
  );

  if (!items.length) {
    return null;
  }

  const {
    menuItem: {
      pinnedSideItems,
    },
  } = scooterAtoms;

  const scoped = resolveScopedStyles(
    <scope>
      <style jsx>{`
        .menu-link
          display inline-flex
          align-items center
          white-space nowrap
          &._current
            cursor default
            pointer-events none
      `}</style>
      <style jsx>{`
        .menu-link
          font ${scooterAtoms.menuLink.font}
          letter-spacing ${scooterAtoms.menuLink.letterSpacing}
          text-transform ${scooterAtoms.menuLink.textTransform}
          color ${scooterAtoms.menuLink.idle.color}
          text-decoration ${scooterAtoms.menuLink.idle.decoration}
          transition color ${theme.animations.hover}

          &:active
            color ${scooterAtoms.menuLink.active.color}
            text-decoration ${scooterAtoms.menuLink.active.decoration}
          &:hover
            color ${scooterAtoms.menuLink.hover.color}
            text-decoration ${scooterAtoms.menuLink.hover.decoration}
          &._current
            color ${scooterAtoms.menuLink.current.color}
            text-decoration ${scooterAtoms.menuLink.current.decoration}
      `}</style>
    </scope>
  );

  const Wrapper = isDesktop ? EmptyWrapper : Scroller;

  return (
    <div className={cx('menu', pinnedSideItems && '_pinnedSideItems')}>
      <style jsx>{`
        .menu-list
          list-style none
          text-align center
          padding 18px 0
          margin 0
          display flex
          :global(.desktop) &
            justify-content space-between
            flex-wrap wrap

        .menu-item
          margin 6px 12px

          ._pinnedSideItems &
            &:first-child
              margin-left 0

            &:last-child
              margin-right 0
      `}
      </style>
      <style jsx>{`
        .menu
          border-top ${scooterAtoms.border}
          border-bottom ${scooterAtoms.border}
      `}
      </style>
      <Wrapper>
        <ul className={cx('menu-list', menuListClassName)}>
          {items.map((item, i) => (
            <li key={i} className='menu-item'>
              <Link
                to={item.link}
                type='blank'
                className={scoped.wrapClassNames('menu-link', currentItemResolved?.link === item.link && '_current')}
              >
                {item.content}
              </Link>
            </li>
          ))}
        </ul>
      </Wrapper>
      <scoped.styles />
    </div>
  );
}

ScooterMenu.propTypes = {
  /** Меню для футера */
  menu: menuPropTypes(),
  /** Текущая ссылка */
  currentItem: PropTypes.object,
  /** Дополнительные пункты меню */
  additionalMenuItems: PropTypes.array,
  /** Номер индекса начиная с которого включаються доп пункты меню */
  additionalMenuItemsIndex: PropTypes.number,
  /** Имя класса для кастомизации меню */
  menuListClassName: PropTypes.string,
  /** @ignore */
  isAmp: PropTypes.bool,
  /** @ignore */
  isDesktop: PropTypes.bool,
  /** @ignore */
  theme: PropTypes.object,
  /** @ignore */
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
  }).isRequired,
};

const ScooterMenuWithHOCs = compose(
  withBreakpoint,
  withTheme,
  withMenu('footer'),
  withRouter,
)(ScooterMenu);
ScooterMenuWithHOCs.displayName = 'ScooterMenu';

export { ScooterMenu as StorybookComponent };
export default ScooterMenuWithHOCs;
